import type { MemberRoleId } from '@wehunt/ui'

/**
 * Maps a stored role string onto the three tiers the Roles panel shows.
 *
 * Two vocabularies share the `userHeaders.<uid>.role` field and both are live
 * in production:
 *
 *   web    — owner / admin / **edit** / **view**
 *   mobile — owner / admin / **member** / **viewOnly**
 *
 * They name the same tiers. Reading only the web set treated `member` as
 * view-only, which is what showed a legacy hunter as View-only in the members
 * list while they were editing the same team's map from their phone.
 *
 * Migrated teams are almost entirely `member` — one sampled production team had
 * 13 of 14 — so this is the common case, not an edge one.
 *
 * Owner maps to `admin` because the tier list has no owner entry; ownership is
 * carried separately on the member's `owner` flag.
 *
 * Anything unrecognised falls to `view`, the least-privileged answer: a role we
 * cannot read is not a reason to grant anything.
 */
export function memberRoleIdFromWire(role: string | null | undefined): MemberRoleId {
  switch ((role ?? '').trim()) {
    case 'owner':
    case 'admin':
      return 'admin'
    case 'edit':
    case 'member':
      return 'edit'
    case 'view':
    case 'viewOnly':
      return 'view'
    default:
      return 'view'
  }
}

/** Whether a stored role string denotes team ownership. */
export function isOwnerWireRole(role: string | null | undefined): boolean {
  return (role ?? '').trim() === 'owner'
}
